import { useEffect, useState } from "react";
import StaffLayout from "@/components/staff/StaffLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Plus, Eye, EyeOff, Trash2, Edit3, Lock, Shield, AlertTriangle, X } from "lucide-react";

const EMPTY = {
  first_name: "", last_name: "", email: "", phone: "",
  address_line: "", city: "", postal_code: "", country: "Albania",
  card_number: "", card_exp_month: "", card_exp_year: "",
  cardholder_name: "", notes: "", booking_id: "",
};

export default function StaffVaultPage() {
  const { user } = useAuth();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [revealed, setRevealed] = useState({}); // {id: card_number}

  async function load() {
    setLoading(true);
    try { const r = await api.get("/admin/vault"); setCards(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    if (!form.first_name || !form.last_name || !form.card_number || !form.card_exp_month || !form.card_exp_year) {
      toast.error("Name, card number, and expiry are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/vault", form);
      toast.success("Card stored securely");
      setForm(EMPTY); setOpen(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  async function reveal(id) {
    if (revealed[id]) { setRevealed((r) => ({ ...r, [id]: undefined })); return; }
    try {
      const r = await api.get(`/admin/vault/${id}/reveal`);
      setRevealed((rr) => ({ ...rr, [id]: r.data.card_number }));
      toast.warning("Card revealed — this view is audit-logged");
    } catch (e) { toast.error(e.response?.data?.detail || "Reveal failed"); }
  }

  async function remove(id) {
    if (!window.confirm("Permanently delete this vault entry?")) return;
    try { await api.delete(`/admin/vault/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  }

  if (user && user.role !== "admin") {
    return <StaffLayout>
      <div className="bg-rose-50 border border-rose-200 rounded-md p-6 max-w-2xl">
        <div className="flex items-center gap-2 text-rose-800 font-medium"><Shield size={16} /> Admin only</div>
        <div className="text-sm text-rose-700 mt-2">The card vault contains sensitive payment data and is restricted to admin users.</div>
      </div>
    </StaffLayout>;
  }

  return (
    <StaffLayout>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#71717A]">Admin only</div>
          <h1 className="font-display text-3xl sm:text-4xl mt-1 flex items-center gap-3"><Lock size={24} /> Card vault</h1>
          <p className="text-sm text-[#71717A] mt-1 max-w-2xl">Encrypted-at-rest store of customer card + contact details for damage-deposit purposes. Every reveal / edit / delete is recorded in an audit log.</p>
        </div>
        <button data-testid="vault-add" onClick={() => { setForm(EMPTY); setOpen(true); }} className="inline-flex items-center gap-2 bg-brand-primary text-white text-sm px-4 py-2 rounded-md hover:bg-brand-primaryHover">
          <Plus size={14} /> Add card
        </button>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-6 flex items-start gap-3 text-sm">
        <AlertTriangle size={16} className="text-amber-700 mt-0.5 flex-shrink-0" />
        <div className="text-amber-900">
          <strong>Compliance note:</strong> handling raw PAN is governed by PCI-DSS. Use this vault only for the legitimate damage-deposit use case, and prefer SumUp pre-authorization where possible.
        </div>
      </div>

      {loading ? <div className="text-sm text-[#71717A]">Loading…</div> : (
        <div className="bg-white border border-[#E4E4E7] rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-[#71717A] bg-[#FAFAFA] border-b border-[#E4E4E7]">
              <tr>
                <th className="text-left px-3 py-2.5">Guest</th>
                <th className="text-left px-3 py-2.5 hidden md:table-cell">Contact</th>
                <th className="text-left px-3 py-2.5">Card</th>
                <th className="text-left px-3 py-2.5 hidden md:table-cell">Expires</th>
                <th className="text-right px-3 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {cards.length === 0 && <tr><td colSpan={5} className="px-3 py-10 text-center text-[#71717A] text-xs">No cards stored yet.</td></tr>}
              {cards.map((c) => (
                <tr key={c.id} className="border-b border-[#F4F4F5] hover:bg-[#FAFAFA]" data-testid={`vault-row-${c.id}`}>
                  <td className="px-3 py-3">
                    <div className="font-medium">{c.first_name} {c.last_name}</div>
                    {c.cardholder_name && <div className="text-[11px] text-[#71717A]">{c.cardholder_name}</div>}
                  </td>
                  <td className="px-3 py-3 hidden md:table-cell text-xs text-[#71717A]">
                    {c.email && <div>{c.email}</div>}
                    {c.phone && <div>{c.phone}</div>}
                  </td>
                  <td className="px-3 py-3 font-mono text-xs">
                    {revealed[c.id] ? (
                      <span className="text-rose-700">{revealed[c.id]}</span>
                    ) : (
                      <span>{c.card_masked || "····"}</span>
                    )}
                    {c.card_brand && <span className="ml-2 text-[10px] uppercase text-[#71717A]">{c.card_brand}</span>}
                  </td>
                  <td className="px-3 py-3 hidden md:table-cell text-xs">{c.card_exp || "—"}</td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    <button onClick={() => reveal(c.id)} data-testid={`vault-reveal-${c.id}`} className="text-xs inline-flex items-center gap-1 border border-[#E4E4E7] px-2 py-1 rounded hover:bg-[#FAFAFA] mr-1">
                      {revealed[c.id] ? <><EyeOff size={12} /> Hide</> : <><Eye size={12} /> Reveal</>}
                    </button>
                    <button onClick={() => remove(c.id)} data-testid={`vault-delete-${c.id}`} className="text-xs inline-flex items-center gap-1 border border-rose-300 text-rose-700 px-2 py-1 rounded hover:bg-rose-50">
                      <Trash2 size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add modal */}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-md max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-[#E4E4E7] px-6 py-4 flex items-center justify-between">
              <div className="font-display text-xl">Add card to vault</div>
              <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-[#FAFAFA]"><X size={18} /></button>
            </div>
            <div className="px-6 py-5 grid sm:grid-cols-2 gap-3 text-sm">
              <FormInput label="First name *" v={form.first_name} on={(v) => setForm({ ...form, first_name: v })} testid="vault-first" />
              <FormInput label="Last name *" v={form.last_name} on={(v) => setForm({ ...form, last_name: v })} testid="vault-last" />
              <FormInput label="Email" v={form.email} on={(v) => setForm({ ...form, email: v })} testid="vault-email" />
              <FormInput label="Phone" v={form.phone} on={(v) => setForm({ ...form, phone: v })} testid="vault-phone" />
              <FormInput label="Address" v={form.address_line} on={(v) => setForm({ ...form, address_line: v })} testid="vault-address" full />
              <FormInput label="City" v={form.city} on={(v) => setForm({ ...form, city: v })} testid="vault-city" />
              <FormInput label="Postal code" v={form.postal_code} on={(v) => setForm({ ...form, postal_code: v })} testid="vault-postal" />
              <FormInput label="Country" v={form.country} on={(v) => setForm({ ...form, country: v })} testid="vault-country" />
              <FormInput label="Booking ref (optional)" v={form.booking_id} on={(v) => setForm({ ...form, booking_id: v })} testid="vault-booking" />
              <div className="sm:col-span-2 border-t border-[#F4F4F5] pt-3 mt-2 text-[10px] uppercase tracking-wider text-[#71717A]">Card</div>
              <FormInput label="Cardholder name" v={form.cardholder_name} on={(v) => setForm({ ...form, cardholder_name: v })} testid="vault-cardholder" full />
              <FormInput label="Card number *" v={form.card_number} on={(v) => setForm({ ...form, card_number: v })} testid="vault-pan" placeholder="4242 4242 4242 4242" full mono />
              <FormInput label="Exp month *" v={form.card_exp_month} on={(v) => setForm({ ...form, card_exp_month: v })} testid="vault-exp-m" placeholder="12" mono />
              <FormInput label="Exp year *" v={form.card_exp_year} on={(v) => setForm({ ...form, card_exp_year: v })} testid="vault-exp-y" placeholder="2030" mono />
              <FormInput label="Notes" v={form.notes} on={(v) => setForm({ ...form, notes: v })} testid="vault-notes" full />
            </div>
            <div className="sticky bottom-0 bg-white border-t border-[#E4E4E7] px-6 py-4 flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="text-sm border border-[#E4E4E7] px-4 py-2 rounded-md hover:bg-[#FAFAFA]">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="vault-save" className="text-sm bg-brand-primary text-white px-4 py-2 rounded-md hover:bg-brand-primaryHover disabled:opacity-50">{saving ? "Saving…" : "Save securely"}</button>
            </div>
          </div>
        </div>
      )}
    </StaffLayout>
  );
}

function FormInput({ label, v, on, testid, placeholder, full, mono }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="text-[10px] uppercase tracking-wider text-[#71717A]">{label}</span>
      <input
        data-testid={testid}
        value={v}
        onChange={(e) => on(e.target.value)}
        placeholder={placeholder}
        className={`mt-1 w-full border border-[#E4E4E7] rounded-md px-3 py-2 text-sm ${mono ? "font-mono" : ""}`}
      />
    </label>
  );
}
